import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client, { imgUrl, logoUrl, assetUrl } from "@/lib/glossyApi";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LayoutGrid, Users, Newspaper, Share2, Sparkles, Image as ImageIcon,
  Check, Trash2, RefreshCw, Send, X, LogOut, Plus, Pencil, Upload, Save, Eye, Megaphone, Star, PenLine, Zap, Gift, ArrowLeft, Tag, ExternalLink, Table2, Archive as ArchiveIcon, Clock, ChevronUp, ChevronDown, FileText, Globe,
} from "lucide-react";
import { LONDON_AREAS } from "@/lib/londonAreas";
import ShareKit from "@/components/ShareKit";
import WebsiteImport, { mergeProfile } from "@/components/WebsiteImport";
import { cycleIndexFor, editionLabel, editionStartIso, issueNoFor, upcomingEditions } from "@/lib/issue";
import { AD_SLOT_META, CATEGORIES, SECTIONS, TONES, LENGTHS, MCATS, BUSINESS_CATS, Header, Stat, InboxGroup, InboxRow, DIn, DArea, Badge, Panel, Drawer, Field, Area, Select, ComboField } from "./shared";

export default function Partners() {
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState({ brand: "", title: "", perk: "", description: "", category: "Partner", cta_label: "Claim offer", cta_url: "", accent: "#1a1a1a" });
  const [busy, setBusy] = useState(false);
  const load = () => client.get("/admin/partner-offers").then((r) => setOffers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.brand.trim() || !form.title.trim()) { toast.error("Add a brand and title."); return; }
    setBusy(true);
    try { await client.post("/admin/partner-offers", { ...form, status: "published" }); toast.success("Partner offer added."); setForm({ brand: "", title: "", perk: "", description: "", category: "Partner", cta_label: "Claim offer", cta_url: "", accent: "#1a1a1a" }); await load(); }
    catch { toast.error("Failed."); }
    setBusy(false);
  };
  const remove = async (o) => { await client.delete(`/admin/partner-offers/${o.id}`); toast.success("Removed."); load(); };

  return (
    <div>
      <Header kicker="Members' Privileges" title="Partner Offers" />
      <div className="p-6 sm:p-10">
        <div className="bg-ink text-cream p-6 sm:p-8 grain mb-10" data-testid="partner-create-panel">
          <div className="relative z-10 space-y-4">
            <p className="overline text-gloss-gold">Add a partner offer</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Brand e.g. Spotify" className="bg-transparent border border-cream/30 px-4 py-3 font-sans focus:outline-none focus:border-gloss-gold" data-testid="partner-brand" />
              <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Category" className="bg-transparent border border-cream/30 px-4 py-3 font-sans focus:outline-none" data-testid="partner-category" />
            </div>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Offer title e.g. 3 months Premium free" className="w-full bg-transparent border border-cream/30 px-4 py-3 font-sans focus:outline-none focus:border-gloss-gold" data-testid="partner-title" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input value={form.perk} onChange={(e) => setForm({ ...form, perk: e.target.value })} placeholder="Perk summary" className="bg-transparent border border-cream/30 px-4 py-3 font-sans focus:outline-none" data-testid="partner-perk" />
              <div className="flex items-center gap-3 border border-cream/30 px-4 py-2">
                <span className="overline text-cream/60">Accent</span>
                <input type="color" value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} className="w-10 h-8 bg-transparent cursor-pointer" data-testid="partner-accent" />
              </div>
            </div>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description…" rows={2} className="w-full bg-transparent border border-cream/30 px-4 py-3 font-sans focus:outline-none resize-none" data-testid="partner-description" />
            <div className="flex flex-col sm:flex-row gap-4">
              <input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} placeholder="CTA label" className="flex-1 bg-transparent border border-cream/30 px-4 py-3 font-sans focus:outline-none" data-testid="partner-cta-label" />
              <input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} placeholder="CTA URL" className="flex-1 bg-transparent border border-cream/30 px-4 py-3 font-sans focus:outline-none" data-testid="partner-cta-url" />
              <button onClick={create} disabled={busy} className="bg-cream text-ink px-6 py-3 overline flex items-center justify-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid="partner-create">
                {busy ? <RefreshCw size={16} className="animate-spin" /> : <Plus size={16} />} Add
              </button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="partners-grid">
          {offers.map((o) => (
            <div key={o.id} className="text-cream p-6 flex flex-col" style={{ backgroundColor: o.accent || "#1a1a1a" }}>
              <div className="flex items-center justify-between mb-3">
                <span className="overline text-cream/60">{o.brand}</span>
                <button onClick={() => remove(o)} className="p-1.5 hover:text-red-400 transition-colors" aria-label="Delete"><Trash2 size={15} /></button>
              </div>
              <h3 className="font-serif text-xl tracking-tight">{o.title}</h3>
              <p className="font-sans text-sm text-cream/70 mt-2 flex-1">{o.description}</p>
              <p className="font-mono text-xs text-cream/50 mt-3">{o.perk} · {o.category}</p>
            </div>
          ))}
          {offers.length === 0 && <p className="px-5 py-10 text-center font-sans text-ink/50 lg:col-span-3">No partner offers yet.</p>}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Drip Engine ---------------- */
